import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import filterSort from 'bcl/helpers/filter-sort';
import { get, fn } from '@ember/helper';
import Store from '@ember-data/store';
import type ItemModel from 'bcl/models/item';
import type SupplierItemModel from 'bcl/models/supplier-item';
import type ProductGroupModel from 'bcl/models/product-group';
import SupplierModel from 'bcl/models/supplier';

interface BasicDataItemsSignature {
  // The arguments accepted by the component
  Args: {
    model: ItemModel[];
  };
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}

export default class BasicDataItemsComponent extends Component<BasicDataItemsSignature> {
  @service declare store: Store;
  @service declare router: RouterService;

  get sortedItems() {
    const items = [...this.args.model];
    items.sort((a, b) => a.name?.localeCompare(b.name));
    return items;
  }

  @action
  create() {
    const newModel = this.store.createRecord<ItemModel>('item', {});
    newModel.active = true;
    const pgs = this.store.peekAll('product-group') as ProductGroupModel[];
    const pg = [...pgs].sort((a: ProductGroupModel, b: ProductGroupModel) =>
      a.name?.localeCompare(b.name)
    )[0];
    newModel.product_group = pg as ProductGroupModel;
    const allSupl = this.store.peekAll<SupplierModel>('supplier');
    const firstNonSpecial = allSupl.find((sup) => sup.additional !== 'special');
    if (firstNonSpecial) {
      this.store.createRecord<SupplierItemModel>('supplier-item', {
        supplier: firstNonSpecial,
        item: newModel,
      });
    }

    this.router.transitionTo('auth.basic-data.item', newModel);
  }
  @action
  edit(item: ItemModel) {
    this.router.transitionTo('auth.basic-data.item', item.id);
  }
  <template>
    <h2
      id="items"
      class="scroll-mt-10 text-xl font-semibold leading-7 text-gray-900"
    >Artikel</h2>
    <div class="px-4 lg:px-8">
      <div class="sm:flex sm:items-center">
        <div class="sm:flex-auto">

        </div>
        <div class="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            {{on "click" this.create}}
            class="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >Neuen Artikel anlegen</button>
        </div>
      </div>
      <div class="mt-8 flow-root">
        <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div class="inline-block min-w-full py-2 align-middle px-4 lg:px-8">
            <table class="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th
                    scope="col"
                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                  >Name</th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >Gebinde</th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >Art-Nummer</th>
                  {{! <th
                  scope="col"
                  class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >Role</th> }}
                  <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-3">
                    <span class="sr-only">Edit</span>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white">

                {{#each
                  (filterSort this.sortedItems "active" "name")
                  as |item|
                }}
                  <tr class="even:bg-gray-50">
                    <td
                      class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3"
                    >{{item.name}}</td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >{{item.gebinde}}</td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >{{get (get item.supplierItems 0) "articlenr"}}</td>
                    <td
                      class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3"
                    >
                      <button
                        type="button"
                        {{on "click" (fn this.edit item)}}
                        class="text-indigo-600 hover:text-indigo-900"
                      >bearbeiten</button>

                    </td>
                  </tr>
                {{/each}}

                {{! More people... }}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <h2 class="mt-20 text-xl font-semibold ml-4">Deaktivierte Artikel</h2>
    <div class="px-4 px-4 lg:px-8">
      <div class="sm:flex sm:items-center">
        <div class="sm:flex-auto">

        </div>
        <div class="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">

        </div>
      </div>
      <div class="mt-8 flow-root">
        <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div class="inline-block min-w-full py-2 align-middle px-4 lg:px-8">
            <table class="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th
                    scope="col"
                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                  >Name</th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >Gebinde</th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >Art-Nummer</th>
                  {{! <th
                  scope="col"
                  class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >Role</th> }}
                  <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-3">
                    <span class="sr-only">Edit</span>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white">
                {{#each (filterSort @model "!active" "name") as |item|}}
                  <tr class="even:bg-gray-50">
                    <td
                      class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3"
                    >{{item.name}}</td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >{{item.gebinde}}</td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >{{item.firstArticleNr}}</td>
                    {{!-- <td
                    class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                  >{{item.a}}</td> --}}
                    <td
                      class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3"
                    >
                      <button
                        type="button"
                        {{on "click" (fn this.edit item)}}
                        class="text-indigo-600 hover:text-indigo-900"
                      >bearbeiten<span class="sr-only">,
                          {{item.name}}</span></button>
                    </td>
                  </tr>
                {{/each}}

                {{! More people... }}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </template>
}
